import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowLeftRight, Trophy, TrendingUp, ArrowRightLeft, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchTopSites } from '../store/actions/siteActions';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import { getCategoryLabel } from '../utils/categoryDisplay';
import AdBanner from '../components/ads/AdBanner';
import AdCard from '../components/ads/AdCard';
import { bannerAds, cardAds } from '../data/adData';
import styled from 'styled-components';

const AdCardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Header = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const IconBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(220, 38, 38, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.crimson};
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const EmptyWrap = styled.div`
  text-align: center;
  padding: 5rem 0;
`;

const EmptyIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CompareCard = styled.div`
  padding: 2rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.card};
  box-shadow: ${({ theme }) => theme.shadowCard};
  overflow: hidden;
  position: relative;
`;

const CardAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  background: ${({ $crimson, theme }) => ($crimson ? theme.colors.crimson : theme.colors.primary)};
`;

const CardLabel = styled.span`
  font-weight: 700;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ $crimson, theme }) => ($crimson ? theme.colors.crimson : theme.colors.primary)};
`;

const CardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0.5rem 0 0 0;
`;

const CardDomain = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
`;

const CardDesc = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.75rem 0 0 0;
  line-height: 1.5;
`;

const CardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const CardTag = styled.span`
  padding: 0.25rem 0.75rem;
  background: ${({ $crimson, theme }) => ($crimson ? 'rgba(220,38,38,0.1)' : 'rgba(0,48,135,0.1)')};
  color: ${({ $crimson, theme }) => ($crimson ? theme.colors.crimson : theme.colors.primary)};
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const TableWrap = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.primary};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primaryForeground};
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 1rem;
  font-size: 0.875rem;
  background: ${({ $alt }) => ($alt ? 'rgba(0,0,0,0.02)' : 'transparent')};
`;

const TableLabel = styled.span`
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-weight: 500;
`;

const TableValue = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
`;

const WinnerBox = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(234, 179, 8, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const WinnerText = styled.p`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const WinnerSub = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
`;

const BrowseBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: 0.875rem;
  &:hover {
    opacity: 0.9;
  }
`;

const SwapBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primaryForeground};
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalBox = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  padding: 2rem;
  max-width: 28rem;
  width: 100%;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 1.5rem 0;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  color: ${({ theme }) => theme.colors.mutedForeground};
  padding: 0.25rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const ModalSelectWrap = styled.div`
  margin-bottom: 1rem;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin-bottom: 0.5rem;
`;

const ModalSelect = styled.select`
  width: 100%;
  height: 2.75rem;
  padding: 0 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: 0.875rem;
  outline: none;
  border: 2px solid transparent;
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const ModalSubmit = styled.button`
  width: 100%;
  margin-top: 1rem;
  height: 2.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  font-size: 0.875rem;
  &:hover {
    opacity: 0.9;
  }
`;

const SelectSitesBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding: 0.6rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-size: 0.875rem;
  font-weight: 600;
  &:hover {
    opacity: 0.9;
  }
`;

function Compare() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectA, setSelectA] = useState(searchParams.get('a') || '');
  const [selectB, setSelectB] = useState(searchParams.get('b') || '');
  const domainA = searchParams.get('a') || '';
  const domainB = searchParams.get('b') || '';
  const { topSites } = useSelector((state) => state.sites);

  useEffect(() => {
    dispatch(fetchTopSites({ limit: 50 }));
  }, [dispatch]);

  const siteA = topSites.find((s) => s.domain === domainA);
  const siteB = topSites.find((s) => s.domain === domainB);

  const openModal = () => {
    setSelectA(domainA);
    setSelectB(domainB);
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);
  const handleModalCompare = (e) => {
    e.preventDefault();
    if (selectA && selectB && selectA !== selectB) {
      navigate(`/compare?a=${encodeURIComponent(selectA)}&b=${encodeURIComponent(selectB)}`);
      closeModal();
    }
  };

  const formatAlternativeRank = (site) => (site?.alternativeRank != null && site?.alternativeTo ? `#${site.alternativeRank} to ${site.alternativeTo}` : 'N/A');
  const formatUserScore = (site) => (site?.userScore != null ? `${site.userScore}/5` : 'N/A');

  const metrics = [
    { label: 'Alternative Rank', a: formatAlternativeRank(siteA), b: formatAlternativeRank(siteB) },
    { label: 'Trending', a: siteA?.trending || 'N/A', b: siteB?.trending || 'N/A' },
    { label: 'User Score', a: formatUserScore(siteA), b: formatUserScore(siteB) },
    { label: 'Category', a: getCategoryLabel(siteA?.category) || 'N/A', b: getCategoryLabel(siteB?.category) || 'N/A' },
    { label: 'Similarity Score', a: siteA?.similarityScore ? `${siteA.similarityScore}%` : 'N/A', b: siteB?.similarityScore ? `${siteB.similarityScore}%` : 'N/A' },
  ];

  const hasData = siteA || siteB;

  return (
    <PageLayout>
      <SeoHead title="Compare | CiteMatch" description="Compare websites side-by-side with CiteMatch." noindex />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>

        <Header initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <IconBox>
            <ArrowLeftRight size={20} strokeWidth={2.5} />
          </IconBox>
          <Title>Site Comparison</Title>
        </Header>

        {!hasData && (
          <EmptyWrap>
            <EmptyIcon>
              <ArrowLeftRight size={28} />
            </EmptyIcon>
            <p style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Select two sites to compare</p>
            <p style={{ color: 'var(--muted-foreground)', marginTop: '0.5rem' }}>Choose two websites below to compare them side by side.</p>
            <SelectSitesBtn type="button" onClick={openModal}>
              <ArrowLeftRight size={16} /> Select sites to compare
            </SelectSitesBtn>
            <BrowseBtn to={prependPath('/top-sites')}>Browse Top Sites <TrendingUp size={14} /></BrowseBtn>
          </EmptyWrap>
        )}

        {hasData && (
          <>
            <SelectSitesBtn type="button" onClick={openModal} style={{ marginRight: '0.5rem' }}>
              <ArrowLeftRight size={16} /> Change sites
            </SelectSitesBtn>
            {domainA && domainB && (
              <SwapBtn type="button" onClick={() => navigate(`/compare?a=${domainB}&b=${domainA}`)}>
                <ArrowRightLeft size={16} /> Swap sites
              </SwapBtn>
            )}
            <CardsGrid>
              <CompareCard>
                <CardAccent />
                <CardLabel>Original</CardLabel>
                <CardTitle>{siteA?.title || domainA}</CardTitle>
                <CardDomain>{siteA?.domain}</CardDomain>
                <CardDesc>{siteA?.description}</CardDesc>
                <CardTags>
                  {siteA?.tags?.map((tag) => (
                    <CardTag key={tag}>{tag}</CardTag>
                  ))}
                </CardTags>
              </CompareCard>
              <CompareCard>
                <CardAccent $crimson />
                <CardLabel $crimson>Competitor</CardLabel>
                <CardTitle>{siteB?.title || domainB}</CardTitle>
                <CardDomain>{siteB?.domain}</CardDomain>
                <CardDesc>{siteB?.description}</CardDesc>
                <CardTags>
                  {siteB?.tags?.map((tag) => (
                    <CardTag key={tag} $crimson>{tag}</CardTag>
                  ))}
                </CardTags>
              </CompareCard>
            </CardsGrid>

            <div style={{ marginBottom: '2rem' }}>
              <AdBanner ad={bannerAds[0]} variant="inline" />
            </div>

            <TableWrap>
              <TableHeader>
                <span>Metric</span>
                <span>{siteA?.title || domainA}</span>
                <span>{siteB?.title || domainB}</span>
              </TableHeader>
              {metrics.map((m, i) => (
                <TableRow key={m.label} $alt={i % 2 === 1}>
                  <TableLabel>{m.label}</TableLabel>
                  <TableValue>{m.a}</TableValue>
                  <TableValue>{m.b}</TableValue>
                </TableRow>
              ))}
            </TableWrap>

            {siteA && siteB && (
              <WinnerBox>
                <Trophy size={24} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                <div>
                  <WinnerText>
                    {(siteA.similarityScore || 0) >= (siteB.similarityScore || 0)
                      ? `${siteA.title} leads in similarity score`
                      : `${siteB.title} leads in similarity score`}
                  </WinnerText>
                  <WinnerSub>Based on our similarity analysis engine</WinnerSub>
                </div>
              </WinnerBox>
            )}

            <AdCardsRow>
              {cardAds.map((ad) => (
                <AdCard key={ad.id} ad={ad} />
              ))}
            </AdCardsRow>
          </>
        )}

        <AnimatePresence>
          {modalOpen && (
            <ModalOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
            >
              <ModalBox
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'relative' }}
              >
                <ModalClose type="button" onClick={closeModal} aria-label="Close">
                  <X size={20} />
                </ModalClose>
                <ModalTitle>Select two sites to compare</ModalTitle>
                <form onSubmit={handleModalCompare}>
                  <ModalSelectWrap>
                    <ModalLabel>First site</ModalLabel>
                    <ModalSelect value={selectA} onChange={(e) => setSelectA(e.target.value)} required>
                      <option value="">Choose a site</option>
                      {topSites.map((s) => (
                        <option key={s.id} value={s.domain} disabled={s.domain === selectB}>{s.title} ({s.domain})</option>
                      ))}
                    </ModalSelect>
                  </ModalSelectWrap>
                  <ModalSelectWrap>
                    <ModalLabel>Second site</ModalLabel>
                    <ModalSelect value={selectB} onChange={(e) => setSelectB(e.target.value)} required>
                      <option value="">Choose a site</option>
                      {topSites.map((s) => (
                        <option key={s.id} value={s.domain} disabled={s.domain === selectA}>{s.title} ({s.domain})</option>
                      ))}
                    </ModalSelect>
                  </ModalSelectWrap>
                  <ModalSubmit type="submit" disabled={!selectA || !selectB || selectA === selectB}>
                    Compare
                  </ModalSubmit>
                </form>
              </ModalBox>
            </ModalOverlay>
          )}
        </AnimatePresence>
      </Container>
      <Footer />
    </PageLayout>
  );
}

export default Compare;
